import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CircularProgress, Autocomplete } from '@mui/material';
import TextBox from '../../../../old/components/filter/pickAndType/TextBox';
import NoResultsIcon from '../../../../assets/icons/question-inside-chat.svg';

export default function AsynchronousAutoComplete(props) {
  const {
    id = '',
    autoFocus,
    name,
    placeholder,
    value,
    onChanges,
    onKeyDown,
    api,
    exitLocations,
    classes: overrideClasses = {},
    textFieldProps = {},
    autoCompleteProps = {},
    clearableOnSelect = false,
    clearableOnBlur = false,
    transformData,
    textFieldLoadingProps = {},
    appendToSearchQuery = {},
    searchQuery = 'title',
    resetOnSelect = false,
    searchPreventOnEmpty = false,
    variant,
    renderOption,
    isShowOptionOnLoad,
    filterOptions,
    isDisabled = false,
    noResultsLink,
    showDropdownWhenFocus = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [tCommon] = useTranslation('common');
  const inputFocus = useRef();

  const getOptions = useCallback(
    async (params) => {
      setLoading(true);

      if (isShowOptionOnLoad) {
        setOptions([]);
      }

      const newParams = {
        ...params,
        ...appendToSearchQuery,
      };

      if (searchPreventOnEmpty && newParams[searchQuery].trim() === '') {
        setOptions([]);
        return;
      }

      if (
        !isShowOptionOnLoad &&
        newParams[searchQuery].length < 2 &&
        !showDropdownWhenFocus
      ) {
        setOptions([]);
        setLoading(false);
        return;
      }

      const response = await axios.get(api, {
        params: newParams,
      });

      if (exitLocations) {
        let currentList = response.data;

        currentList = currentList.filter((objFromA) => {
          return !exitLocations.find((objFromB) => {
            return objFromA.id === objFromB.countryId;
          });
        });

        setOptions(currentList);
        setLoading(false);
        return;
      }

      setOptions(response.data);
      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, appendToSearchQuery]
  );

  useEffect(() => {
    if (isShowOptionOnLoad) {
      if (inputFocus.current) {
        inputFocus.current.focus();
        setInputFocused(true);
        setOpen(true);
      }
    }

    const triggerFocus = (e) => {
      if (e.detail.name === name) {
        inputFocus.current.focus();
      }
    };
    document.addEventListener('the-input-focus-after-select', triggerFocus);

    return () => {
      document.removeEventListener(
        'the-input-focus-after-select',
        triggerFocus
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newOptions = useMemo(() => {
    if (transformData) {
      return transformData(options, value);
    }

    return options;
  }, [transformData, options, value]);

  useEffect(() => {
    const { selectOnFocus } = autoCompleteProps;

    if (touched && (isUndefined(selectOnFocus) || selectOnFocus === true)) {
      getOptions({
        [searchQuery]: inputValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched]);

  const typingTimeout = useRef(null);
  const onSearchChange = (e) => {
    clearTimeout(typingTimeout.current);
    const searchText = e.target.value;
    typingTimeout.current = setTimeout(() => {
      getOptions({
        [searchQuery]: searchText,
      });
    }, 200);
  };

  const onInputFocus = () => {
    if (!touched) {
      setTouched(true);
    }

    setInputFocused(true);

    if (showDropdownWhenFocus) {
      setOpen(true);
      getOptions({
        [searchQuery]: inputValue,
      });
    } else {
      setOpen(false);
      setOptions([]);
      setLoading(false);
    }
  };

  const onBlur = () => {
    if (clearableOnBlur === true) {
      setOptions([]);
    }

    setInputFocused(false);
    setOptions([]);
    setLoading(false);
  };

  const getRootStyles = () => {
    if (!inputValue) {
      return 'no-value';
    }

    return '';
  };

  return (
    <Autocomplete
      id={id}
      classes={{
        root: getRootStyles(),
        ...overrideClasses,
      }}
      disabled={isDisabled}
      open={
        open &&
        inputFocused &&
        (isShowOptionOnLoad ||
          inputValue.length > 1 ||
          showDropdownWhenFocus) &&
        (!loading || newOptions.length > 0)
      }
      onOpen={() => {
        setOpen(true);
      }}
      onClose={(event, reason) => {
        if (reason === 'create-option') {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (event.currentTarget !== inputFocus.current) {
          setOpen(false);
        }

        if (reason !== 'toggleInput' && resetOnSelect) {
          setTouched(false);
        }
      }}
      onChange={(event, newValue, reason) => {
        if (reason === 'create-option') {
          event.preventDefault();
          event.nativeEvent.stopImmediatePropagation();
          event.stopPropagation();
          return;
        }

        onChanges(name, newValue, newOptions);
        if (clearableOnSelect) {
          setInputValue('');
        }

        if (reason === 'clear' && event.type === 'click') {
          getOptions({ [searchQuery]: '' });
        }
      }}
      value={value}
      // getOptionLabel={(option) => (option.label ? option.label : '')}
      loading={loading}
      inputValue={inputValue}
      noOptionsText={
        searchPreventOnEmpty && inputValue === '' ? null : (
          <div className='async-auto-no-results-wrapper'>
            <img src={NoResultsIcon} alt='no results' />
            &nbsp;
            <span className='message'>{tCommon('labels.noResults')}</span>&nbsp;
            {noResultsLink?.link && (
              <a href={noResultsLink.link} target='_blank' rel='noreferrer'>
                {noResultsLink?.linkName}
              </a>
            )}
          </div>
        )
      }
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={newOptions}
      loadingText={null}
      {...autoCompleteProps}
      renderInput={(params) => (
        <TextBox
          autoFocus={autoFocus}
          variant={variant || undefined}
          name={name}
          {...params}
          {...textFieldProps}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            inputRef: inputFocus,
            endAdornment: (
              <>
                {loading && (!searchPreventOnEmpty || inputValue) ? (
                  <CircularProgress
                    color='inherit'
                    size={14}
                    {...textFieldLoadingProps}
                  />
                ) : (
                  params.InputProps.endAdornment
                )}
              </>
            ),
          }}
          onChange={onSearchChange}
          onFocus={onInputFocus}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (
              e.keyCode === 13 &&
              isUndefined(onKeyDown) === false &&
              isUndefined(value.label) === false &&
              value !== ''
            ) {
              onChanges(name, value, newOptions);
              onKeyDown();
              e.preventDefault();
            }
          }}
        />
      )}
      renderOption={renderOption || autoCompleteProps.renderOption}
      filterOptions={filterOptions || autoCompleteProps.filterOptions}
    />
  );
}
